// client/src/components/agent/actions/QuickRepliesView.jsx
import { useState, useEffect, useCallback } from 'react';
import theme from '../../../theme';
import InteractiveBuilder from './InteractiveBuilder';
import { emptyInteractive, validateInteractive } from './interactiveLimits';

const TYPES = [
  { v: 'texto', label: 'Texto' },
  { v: 'imagen', label: 'Imagen' },
  { v: 'video', label: 'Video' },
  { v: 'documento', label: 'Documento' },
  { v: 'interactivo', label: 'Mensaje interactivo' },
];

// Intenta parsear la config interactiva guardada (description es JSON). Si falla,
// devuelve una config vacía para no romper el formulario.
function parseInteractive(description) {
  try {
    const cfg = JSON.parse(description);
    if (cfg && cfg.action) return cfg;
  } catch { /* ignore */ }
  return emptyInteractive();
}

export default function QuickRepliesView({ token, conv, onBack }) {
  const [items, setItems] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const c = theme.colors;
  const headers = { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };

  const load = useCallback(() => {
    fetch('/api/agent/archives', { headers }).then((r) => r.json()).then(setItems).catch(() => {});
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const send = async (id) => {
    if (!conv) return;
    setSendingId(id);
    try {
      await fetch(`/api/agent/archives/${id}/send`, {
        method: 'POST', headers, body: JSON.stringify({ conversation_id: conv.id }),
      });
    } catch { /* silent */ }
    setSendingId(null);
  };

  const remove = async (id) => {
    await fetch(`/api/agent/archives/${id}`, { method: 'DELETE', headers });
    load();
  };

  return (
    <div style={{ padding: 12, color: c.text, overflowY: 'auto' }}>
      <button onClick={onBack} style={{ background: 'none', border: 'none', color: c.primary, cursor: 'pointer', marginBottom: 8 }}>← Atrás</button>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h4 style={{ margin: 0 }}>Textos y media rápidos</h4>
        <button onClick={() => setShowCreate(true)} style={{ background: c.primary, color: '#001018', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer' }}>Crear</button>
      </div>

      {!conv && (
        <div style={{ color: c.textMuted, fontSize: 12, lineHeight: 1.5, marginBottom: 10 }}>
          Selecciona una conversación para poder enviar estos elementos.
        </div>
      )}

      {items.map((it) => (
        <div key={it.id} style={{ border: `1px solid ${c.border}`, borderRadius: 8, padding: 8, marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600 }}>{it.name}</div>
          <div style={{ fontSize: 11, color: c.textMuted, marginBottom: 6 }}>{it.type}</div>
          <div style={{ display: 'flex', gap: 6 }}>
            <button onClick={() => send(it.id)} disabled={sendingId === it.id || !conv} title={!conv ? 'Selecciona una conversación para enviar' : undefined}
              style={{ flex: 1, background: conv ? c.primary : c.border, color: conv ? '#001018' : c.textMuted, border: 'none', borderRadius: 6, padding: '6px', cursor: conv ? 'pointer' : 'not-allowed' }}>
              {sendingId === it.id ? 'Enviando…' : 'Enviar'}
            </button>
            <button onClick={() => setEditItem(it)} title="Previsualizar / editar"
              style={{ background: 'none', border: `1px solid ${c.border}`, color: c.text, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>👁</button>
            <button onClick={() => remove(it.id)}
              style={{ background: 'none', border: `1px solid ${c.error}`, color: c.error, borderRadius: 6, padding: '6px 10px', cursor: 'pointer' }}>🗑</button>
          </div>
        </div>
      ))}
      {!items.length && <div style={{ color: c.textMuted, fontSize: 13 }}>Sin elementos. Crea uno.</div>}

      {showCreate && <CreateModal token={token} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {editItem && <PreviewEditModal token={token} item={editItem} onClose={() => setEditItem(null)} onSaved={() => { setEditItem(null); load(); }} />}
    </div>
  );
}

// Previsualiza el contenido actual (texto/imagen/video/documento) y permite editarlo.
function InteractivePreview({ description }) {
  const c = theme.colors;
  const cfg = parseInteractive(description);
  const a = cfg.action || {};
  const kindLabel = a.type === 'list' ? 'Lista' : a.type === 'url' ? 'Botón de enlace' : 'Botones';
  return (
    <div style={{ fontSize: 12, color: c.text }}>
      {cfg.header && <div style={{ color: c.textMuted }}>🖼️ Encabezado: {cfg.header.type}</div>}
      <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', margin: '4px 0' }}>{cfg.body}</div>
      {cfg.footer && <div style={{ color: c.textMuted, fontStyle: 'italic' }}>{cfg.footer}</div>}
      <div style={{ color: c.primary, marginTop: 4 }}>
        {kindLabel}: {a.type === 'quick_reply' ? (a.buttons || []).map((b) => b.text).filter(Boolean).join(' · ')
          : a.type === 'url' ? a.text
          : `${a.buttonText} (${(a.rows || []).filter((r) => r.title).length} opciones)`}
      </div>
    </div>
  );
}

function ContentPreview({ type, description }) {
  const c = theme.colors;
  if (!description) return <div style={{ color: c.textMuted, fontSize: 12 }}>Sin contenido aún.</div>;
  if (type === 'interactivo') return <InteractivePreview description={description} />;
  if (type === 'imagen') {
    return <img src={description} alt="" style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, display: 'block' }} />;
  }
  if (type === 'video') {
    return <video src={description} controls style={{ maxWidth: '100%', maxHeight: 220, borderRadius: 8, display: 'block' }} />;
  }
  if (type === 'documento') {
    return <a href={description} target="_blank" rel="noreferrer" style={{ color: c.primary, wordBreak: 'break-all' }}>📄 Abrir documento</a>;
  }
  return <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: c.text, fontSize: 13 }}>{description}</div>;
}

function PreviewEditModal({ token, item, onClose, onSaved }) {
  const [name, setName] = useState(item.name || '');
  const [type, setType] = useState(item.type || 'texto');
  const [description, setDescription] = useState(item.description || '');
  const [cfg, setCfg] = useState(item.type === 'interactivo' ? parseInteractive(item.description) : emptyInteractive());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const c = theme.colors;
  const headers = { Authorization: `Bearer ${token}` };
  const inputStyle = { width: '100%', padding: '8px', background: c.background, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, marginBottom: 8, boxSizing: 'border-box' };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch('/api/agent/archives/upload', { method: 'POST', headers, body: fd });
      const data = await r.json();
      if (data.url) setDescription(data.url);
    } catch { /* silent */ }
    setUploading(false);
  };

  const save = async () => {
    setErr('');
    let desc = description;
    if (type === 'interactivo') {
      const e = validateInteractive(cfg);
      if (e) { setErr(e); return; }
      desc = JSON.stringify(cfg);
    }
    if (!name.trim() || !desc.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`/api/agent/archives/${item.id}`, {
        method: 'PATCH', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, type, description: desc }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setErr(j.message || 'Error al guardar'); }
      else onSaved();
    } catch { setErr('Error de conexión'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: 18, width: '92vw', maxWidth: 360, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <h4 style={{ marginTop: 0, color: c.text }}>Previsualizar / editar</h4>

        <div style={{ border: `1px solid ${c.border}`, borderRadius: 8, padding: 10, marginBottom: 12, background: c.background }}>
          <ContentPreview type={type} description={type === 'interactivo' ? JSON.stringify(cfg) : description} />
        </div>

        <input style={inputStyle} placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
        <select style={inputStyle} value={type} onChange={(e) => { setType(e.target.value); setDescription(''); }}>
          {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        {type === 'texto' ? (
          <textarea style={{ ...inputStyle, minHeight: 80 }} placeholder="Texto a enviar" value={description} onChange={(e) => setDescription(e.target.value)} />
        ) : type === 'interactivo' ? (
          <InteractiveBuilder value={cfg} onChange={setCfg} token={token} />
        ) : (
          <div style={{ marginBottom: 8 }}>
            <input type="file" onChange={(e) => upload(e.target.files[0])} />
            {uploading && <div style={{ color: c.textMuted, fontSize: 12 }}>Subiendo…</div>}
            {description && <div style={{ color: c.success, fontSize: 12, wordBreak: 'break-all' }}>{description}</div>}
          </div>
        )}
        {err && <p style={{ color: c.error, fontSize: 12, margin: '8px 0 0' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${c.border}`, color: c.text, borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>Cerrar</button>
          <button onClick={save} disabled={saving || uploading} style={{ background: c.primary, color: '#001018', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>Guardar</button>
        </div>
      </div>
    </div>
  );
}

function CreateModal({ token, onClose, onCreated }) {
  const [name, setName] = useState('');
  const [type, setType] = useState('texto');
  const [description, setDescription] = useState(''); // texto o url
  const [cfg, setCfg] = useState(emptyInteractive());
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const c = theme.colors;
  const headers = { Authorization: `Bearer ${token}` };
  const inputStyle = { width: '100%', padding: '8px', background: c.background, color: c.text, border: `1px solid ${c.border}`, borderRadius: 6, marginBottom: 8, boxSizing: 'border-box' };

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await fetch('/api/agent/archives/upload', { method: 'POST', headers, body: fd });
      const data = await r.json();
      if (data.url) setDescription(data.url);
    } catch { /* silent */ }
    setUploading(false);
  };

  const save = async () => {
    setErr('');
    let desc = description;
    if (type === 'interactivo') {
      const e = validateInteractive(cfg);
      if (e) { setErr(e); return; }
      desc = JSON.stringify(cfg);
    }
    if (!name.trim() || !desc.trim()) return;
    setSaving(true);
    try {
      const r = await fetch('/api/agent/archives', {
        method: 'POST', headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ name, type, description: desc }),
      });
      if (!r.ok) { const j = await r.json().catch(() => ({})); setErr(j.message || 'Error al guardar'); }
      else onCreated();
    } catch { setErr('Error de conexión'); }
    setSaving(false);
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: c.surface, border: `1px solid ${c.border}`, borderRadius: 12, padding: 18, width: '92vw', maxWidth: 360, maxHeight: '90vh', overflowY: 'auto', boxSizing: 'border-box' }}>
        <h4 style={{ marginTop: 0, color: c.text }}>Crear rápido</h4>
        <input style={inputStyle} placeholder="Nombre" value={name} onChange={(e) => setName(e.target.value)} />
        <select style={inputStyle} value={type} onChange={(e) => { setType(e.target.value); setDescription(''); }}>
          {TYPES.map((t) => <option key={t.v} value={t.v}>{t.label}</option>)}
        </select>
        {type === 'texto' ? (
          <textarea style={{ ...inputStyle, minHeight: 80 }} placeholder="Texto a enviar" value={description} onChange={(e) => setDescription(e.target.value)} />
        ) : type === 'interactivo' ? (
          <InteractiveBuilder value={cfg} onChange={setCfg} token={token} />
        ) : (
          <div style={{ marginBottom: 8 }}>
            <input type="file" onChange={(e) => upload(e.target.files[0])} />
            {uploading && <div style={{ color: c.textMuted, fontSize: 12 }}>Subiendo…</div>}
            {description && <div style={{ color: c.success, fontSize: 12, wordBreak: 'break-all' }}>{description}</div>}
          </div>
        )}
        {err && <p style={{ color: c.error, fontSize: 12, margin: '8px 0 0' }}>{err}</p>}
        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 8 }}>
          <button onClick={onClose} style={{ background: 'none', border: `1px solid ${c.border}`, color: c.text, borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>Cancelar</button>
          <button onClick={save} disabled={saving || uploading} style={{ background: c.primary, color: '#001018', border: 'none', borderRadius: 6, padding: '6px 12px', cursor: 'pointer' }}>Guardar</button>
        </div>
      </div>
    </div>
  );
}
